/**
 * Versioned preferences (language/sector filters).
 * Schema v1 — future features plug in here without rewriting callers.
 *
 * Languages are exclusive: either one language or all. No partial multi-select.
 */
import { LANGUAGES, SECTORS } from "./config.js";

const KEY = "rosetta:prefs:v1";
const LANG_CODES = LANGUAGES.map((l) => l.code);
const SECTOR_CODES = SECTORS.map((s) => s.code);

const DEFAULTS = {
  v: 1,
  langs: [...LANG_CODES],
  sectors: [...SECTOR_CODES],
};

/** Coerce to either a single language or the full set. */
function sanitizeLangs(langs) {
  const allowed = new Set(LANG_CODES);
  const next = (Array.isArray(langs) ? langs : []).filter((c) => allowed.has(c));
  if (next.length === 1) return next;
  return [...DEFAULTS.langs];
}

function sanitizeSectors(sectors) {
  const allowed = new Set(SECTOR_CODES);
  const next = (Array.isArray(sectors) ? sectors : []).filter((c) => allowed.has(c));
  return next.length ? SECTOR_CODES.filter((c) => next.includes(c)) : [...DEFAULTS.sectors];
}

export function isAllLanguages(langs) {
  return !langs || langs.length !== 1;
}

/** Click a language chip: select only that lang, or clear back to all. */
export function selectLanguage(currentLangs, code) {
  if (currentLangs?.length === 1 && currentLangs[0] === code) {
    return [...DEFAULTS.langs];
  }
  return [code];
}

export function loadPrefs() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS, langs: [...DEFAULTS.langs], sectors: [...DEFAULTS.sectors] };
    const data = JSON.parse(raw);
    if (!data || data.v !== 1) return { ...DEFAULTS, langs: [...DEFAULTS.langs], sectors: [...DEFAULTS.sectors] };
    return {
      v: 1,
      langs: sanitizeLangs(data.langs),
      sectors: sanitizeSectors(data.sectors),
    };
  } catch {
    return { ...DEFAULTS, langs: [...DEFAULTS.langs], sectors: [...DEFAULTS.sectors] };
  }
}

export function savePrefs(prefs) {
  const next = {
    v: 1,
    langs: sanitizeLangs(prefs.langs),
    sectors: sanitizeSectors(prefs.sectors),
  };
  localStorage.setItem(KEY, JSON.stringify(next));
  return next;
}

export function toggleInList(list, code, allCodes) {
  const set = new Set(list);
  if (set.has(code)) {
    if (set.size === 1) return list;
    set.delete(code);
  } else {
    set.add(code);
  }
  return allCodes.filter((c) => set.has(c));
}
